<script setup lang="ts">
import { computed, onMounted, onUnmounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { vOnClickOutside } from '@vueuse/components'
import { Geolocation } from '@capacitor/geolocation'
import { useVuelidate } from '@vuelidate/core'
import { required } from '@vuelidate/validators'
// import { useSentry } from '@/plugins/sentry'
import debounce from '@/utils/asyncDebounce'
// import {useGa} from '@/composables/useGa'
import { useResizeObserver } from '@/composables/resizeObserver'
import { useGoogleMaps } from '@/composables/googleMaps'
import Icon from '@/components/partials/Icon.vue'
import GeoLocationButton from './GeoLocationButton.vue'
import Predictions from './Predictions.vue'
import { useSessionStore } from "@/stores/useSessionStore"
import { getCountry } from "@/router/utils/getCountry"
import { storeToRefs } from "pinia"
import type { Location } from "@/types/Location"

const input = ref(null)
const sessionStore = useSessionStore()
// const ga = useGa()
const { t, locale } = useI18n()

const props = withDefaults(defineProps<{
  disableAutoFocus?: boolean
  manualSearch?: boolean
}>(), {
  disableAutoFocus: false,
  manualSearch: false,
})

type SubmitSearch = {
  query: string
  location: {
    street: string
    houseNumber: string | null
    city: string
    zipcode: string
    country: string
    coordinates: {
      lat: number
      lng: number
    }
  }
}

const emit = defineEmits<{
  (e: 'submit', value: { query: string, location: Location }): void
  (e: 'manualSearch'): void
}>()

const state = reactive({
  index: 0,
  predictions: [],
  open: false,
  loading: false,
  hasAskedForLocation: false,
  error: null,
  place: null,
  lat: null,
  long: null,
})

const { location: storedLocation } = storeToRefs(sessionStore)

const location = [
  'street',
  'houseNumber',
  'zipcode',
  'city',
].reduce((acc, key, index) => {
  if (storedLocation.value === null || storedLocation.value[key] === '') {
    return acc
  }

  if (index === 0) {
    return storedLocation.value[key]
  }

  return `${acc} ${storedLocation.value[key]}`
}, '')

const inputValue = ref(storedLocation.value !== null && storedLocation.value.country === getCountry().value ? location : '')

const rules = computed(() => ({
  inputValue: {
    required,
  },
}))

const v = useVuelidate(rules, { inputValue }, { $registerAs: 'location-search' })

const { places, geocode } = useGoogleMaps()

const gatherPredictions = async (value) => {
  if (!value) {
    state.predictions = []
    state.error = null
    state.open = false

    return []
  }

  // note: zipcode search only works for NL
  const type = getCountry().value === 'nl' ? 'geocode' : 'address'

  const { predictions } = await places(value, [ type ], locale.value, getCountry().value)

  if (predictions.length > 0) {
    state.predictions = predictions
    state.error = null
  } else {
    state.predictions = []
    state.error = t('home_header.component_location_search.error_no_results')
  }

  return predictions
}

const debouncedGatherPredictions = debounce(gatherPredictions, 350)

const autocomplete = async () => {
  // unselect any prediction
  state.index = -1

  if (!inputValue.value) {
    return state.predictions = []
  }

  state.loading = true
  state.open = true

  await debouncedGatherPredictions(inputValue.value)

  state.loading = false
}

const updateInputValue = ({ address_components }) => {
  const postalCode = address_components.find(comp => comp.types.includes('postal_code'))
  const route = address_components.find(comp => comp.types.includes('route'))
  const streetNumber = address_components.find(comp => comp.types.includes('street_number'))
  inputValue.value = [ route?.long_name ?? '', streetNumber?.long_name ?? '', postalCode?.long_name ?? '' ].filter(value => value).join(' ')
}

const useGeoLocation = async () => {
  state.loading = true

  try {
    const { coords: { latitude, longitude } } = await Geolocation.getCurrentPosition({
      enableHighAccuracy: true,
      timeout: 5000,
      maximumAge: 0,
    })

    const places = await geocode({ lat: latitude, lng: longitude })
    const [ place ] = places

    state.place = place
    state.lat = latitude
    state.lng = longitude

    updateInputValue(place)

    // ga.trackAddressSearchedLocationPin()

    state.error = null
    state.open = false
  } catch (e) {
    state.error = t('errors.zipcode.geo-location')
    state.open = true

    // sentry?.captureException(e)
  } finally {
    state.loading = false
  }
}

const onInputFocus = async () => {
  if (!inputValue.value && !state.hasAskedForLocation) {
    await useGeoLocation()
    state.hasAskedForLocation = true
  }

  state.open = true
  autocomplete()
}

const submit = async () => {
  // save the query as we might update it later
  const query = inputValue.value

  // If the application is busy, the button is disabled and hitting enter should not submit the form as well
  if (state.loading) return
  if (!inputValue.value) return state.error = t('home_header.component_location_search.error_no_input')

  const hasValidStoreData = storedLocation.value?.coordinates?.lat && storedLocation.value?.coordinates?.lng && storedLocation.value?.street

  // If the value is prefilled using the store and no predictions are gathered, use the store data if sufficient
  if (!state.predictions && hasValidStoreData) {
    return continueWithStoreData()
  }

  // Always gather the latest predictions and select the active one
  const predictions = state.predictions.length ? state.predictions : await gatherPredictions(inputValue.value)
  const prediction = predictions[state.index >= 0 ? state.index : 0]

  if (!prediction) {
    return (state.error = t('home_header.component_location_search.error_no_results'))
  }

  // Resolve the prediction into a place
  const [ place ] = await geocode({ placeId: prediction.place_id }) || []

  if (!place) {
    return (state.error = t('home_header.component_location_search.error_no_results'))
  }

  const { address_components, geometry: { location } } = place
  const street = address_components.find(comp => comp.types.includes('route'))
  const streetNumber = address_components.find(comp => comp.types.includes('street_number'))
  const postalCode = address_components.find(comp => comp.types.includes('postal_code'))
  const city = address_components.find(comp => comp.types.includes('locality'))
  const country = address_components.find(comp => comp.types.includes('country'))

  // ga.trackAddressSearched()

  emit('submit', {
    query,
    location: {
      street: street?.long_name ?? '',
      houseNumber: streetNumber?.long_name ?? '',
      zipcode: postalCode?.long_name ?? '',
      city: city?.long_name ?? '',
      country: country?.short_name.toLowerCase(),
      coordinates: {
        lat: location.lat(),
        lng: location.lng(),
      },
    },
  })
}

const onPredictionSelect = async (prediction) => {
  const [ place ] = await geocode({ placeId: prediction.place_id }) || []

  if (!place) return

  state.place = place
  state.lat = place.geometry.location.lat()
  state.lng = place.geometry.location.lng()

  updateInputValue(place)
  state.open = false

  submit()
}

const handleKeyPress = (e) => {
  // Filter keys
  if (![ 'ArrowDown', 'ArrowUp', 'Enter' ].includes(e.key)) return

  // Stop cursor from jumping all the way to the beginning or the end of the input value
  e.preventDefault()

  if (e.key === 'ArrowDown' && state.open) {
    if (state.index < state.predictions.length - 1) state.index += 1
  }

  if (e.key === 'ArrowUp' && state.open) {
    if (state.index > 0) state.index -= 1
    else input.value.focus()
  }

  if (e.key === 'Enter') {
    // When enter is used on input element
    if (e.target === input.value && !state.loading) {
      submit()
    }

    // When enter is used in predictions dropdown
    if (state.open && state.index > -1) {
      state.open = false
      input.value.focus()

      if (state.predictions[state.index]) {
        onPredictionSelect(state.predictions[state.index])
      }
    }
  }
}

// Watch the document's active element and sync the index with the focussed element if needed
const handleFocusChange = () => {
  if (!document.activeElement.dataset.placeId) return
  state.index = state.predictions.findIndex((place) => place.place_id === document.activeElement.dataset.placeId)
}

const continueWithStoreData = () => {
  emit('submit', {
    query: '',
    location: {
      street: storedLocation.value?.street ?? '',
      houseNumber: storedLocation.value?.houseNumber ?? null,
      city: storedLocation.value?.city ?? '',
      zipcode: storedLocation.value?.zipcode ?? '',
      country: storedLocation.value?.country ?? '',
      coordinates: storedLocation.value.coordinates,
    },
  })
}

const { val } = useResizeObserver()

onMounted(async () => {
  // focus input on desktop
  if (val.value >= 992 && !props.disableAutoFocus) {
    input.value.focus()
  }

  // Resolve saved lat & lng to a street name to support legacy data
  if (storedLocation.value?.coordinates?.lat && storedLocation.value?.coordinates?.lng && !storedLocation.value?.street) {
    const [ place ] = await geocode({
      lat: storedLocation.value.coordinates.lat,
      lng: storedLocation.value.coordinates.lng,
    }) || []
    state.place = place

    const { address_components } = place
    const street = address_components.find(comp => comp.types.includes('route'))
    const streetNumber = address_components.find(comp => comp.types.includes('street_number'))

    // Update the input value
    inputValue.value = [ street?.long_name, streetNumber?.longName ].filter((value) => value).join(' ')
  }

  document.addEventListener('keydown', handleKeyPress)
  document.addEventListener('focusin', handleFocusChange, true)
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyPress)
  document.removeEventListener('focusin', handleFocusChange)
})

function hidePopup() {
  state.open = false
}
</script>

<template>
  <div v-on-click-outside="hidePopup" class="location-search">
    <div :class="[ 'location-search__form', { 'location-search--error': false } ]">
      <div class="location-search__input-container">
        <input id="location-search-input"
               ref="input"
               v-model="v.inputValue.$model"
               data-test-id="location-search-input"
               class="location-search__input"
               :placeholder="t('home_header.component_location_search.placeholder')"
               tabindex="0"
               @input="autocomplete"
               @click="onInputFocus">
        <GeoLocationButton :loading="state.loading" @click="useGeoLocation"/>
      </div>
      <button data-test-id="location-search-button"
              class="location-search__button"
              :class="{ 'location-search__button--disabled': state.loading || !inputValue }"
              tabindex="0"
              @click="submit">
        <span class="location-search__button-content">
          <span class="location-search__button-text">
            {{ t('home_header.component_location_search.search_button_text') }}
          </span>
          <Icon class="location-search__arrow-icon" name="arrow"/>
        </span>
      </button>
    </div>

    <Predictions class="location-search__predictions"
                 :open="state.open || state.error"
                 :predictions="state.predictions"
                 :highlighted-prediction="state.index"
                 @select="onPredictionSelect">
      <div v-if="state.error"
           class="location-search__error"
           data-test-id="location-search-error">
        <Icon name="block"/>
        {{ state.error }}
        <span data-test-id="location-search-manual-modal-trigger"
              class="location-search__error-action"
              @click="emit('manualSearch')"
              v-if="manualSearch">
          {{ t('home_header.component_location_search.error_action_manual_search') }}
        </span>
      </div>
    </Predictions>
  </div>
</template>

<style lang="scss" scoped>
@import "@/assets/css/base/forms.scss";
@import "@/assets/css/variables/variables.scss";
@import "@/assets/css/mixins/breakpoints-up.scss";
@import "@/assets/css/mixins/styling.scss";

$show-button-text-breakpoint: 446px;

.location-search {
  $self: &;
  position: relative;

  &__form {
    position:   relative;
    display:    flex;
    z-index:    50;
    width:      100%;
    margin:     0 auto;
    border-radius: 40rem;
    box-shadow: 0px 7px 15px -10px rgba(0, 0, 0, 0.15);

    @include lg-up {
      margin: 0;
    }
  }

  &__input-container {
    flex-grow: 1;
  }

  &__input {
    width:                  100%;
    padding:                1.25rem 2rem 1.25rem 4rem;
    border:                 1px solid $new-color-neutral-quaternary;
    border-right:           transparent;
    border-bottom-left-radius: 10rem;
    border-top-left-radius: 10rem;
    line-height:            1.4;
    transition:             border 0.2s ease-in-out;

    &::placeholder {
      color: $new-color-neutral-secondary;
    }

    &:focus,
    &:active {
      border: 1px solid $new-color-brand-pink;
      border-right: transparent;
    }
  }

  &__button {
    @include gradient-button;
    display:                 flex;
    position:                relative;
    align-items:             center;
    padding:                 0.875rem 2rem 0.875rem 2rem;
    border-bottom-right-radius: 10rem;
    border-top-right-radius: 10rem;
    cursor:                  pointer;

    @media screen and (min-width: $show-button-text-breakpoint) {
      padding: 0.875rem 1rem 0.875rem 1.5rem;
    }

    &::before {
      content:                   "";
      width:                     110%;
      height:                    120%;
      background:                $new-gradient-brand-main;
      border-top-left-radius:    10rem;
      border-bottom-left-radius: 10rem;
      border-top-right-radius:   50rem;
      border-bottom-right-radius: 50rem;
      position:                  absolute;
      top:                       50%;
      left:                      50%;
      opacity:                   0.2;
      pointer-events:            none;
      z-index:                   0;
      transition:                all 0.2s ease-in;
      transform:                 translate(-50%, -50%) scale(0);
    }

    &:focus,
    &:hover {
      &::before {
        transform: translate(-50%, -50%) scale(1);
      }
    }

    &--disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  }

  &__arrow-icon {
    transform: rotate(-90deg) scale(0.9);

    &:deep(path) {
      fill: #ffffff;
    }
  }

  &__button-content {
    display:  flex;
    gap:      1.5rem;
    color:    #ffffff;
    font-weight: 700;
    z-index:  2;
    position: relative;
  }

  &__button-text {
    display: none;

    @media screen and (min-width: $show-button-text-breakpoint) {
      display: block;
    }
  }

  &__error {
    display: flex;
    padding: 1rem 2rem 1.5rem 2rem;
    gap:     1rem;
    align-items: center;
  }

  &__error-action {
    font-weight: 700;
    color:  $new-color-brand-red;
    cursor: pointer;
    margin-left: auto;
  }

  &--error {
    #{ $self }__input {
      color: $new-color-brand-red;
    }
  }
}
</style>
